import { api } from './api'

export interface QueueMetrics {
  name: string
  waiting: number
  active: number
  completed: number
  failed: number
  delayed: number
  paused: boolean
  total: number
}

export interface QueueStatusResponse {
  success: boolean
  timestamp: string
  queues: QueueMetrics[]
}

export interface QueueStatsResponse {
  success: boolean
  queueName: string
  stats: {
    waiting: number
    active: number
    completed: number
    failed: number
    delayed: number
    paused: boolean
    total: number
  }
}

export interface QueueJob {
  id: string
  name: string
  data: any
  progress: number
  attemptsMade: number
  timestamp: number
  processedOn?: number
  finishedOn?: number
  failedReason?: string
}

export interface QueueJobsResponse {
  success: boolean
  queueName: string
  status: string
  count: number
  jobs: QueueJob[]
}

export const queueService = {
  async getAllQueuesStatus(): Promise<QueueStatusResponse> {
    const response = await api.get<{ success: boolean; data: QueueStatusResponse; message: string }>('/admin/queues/status')
    return response.data.data
  },

  async getQueueStats(queueName: string): Promise<QueueStatsResponse> {
    const response = await api.get<QueueStatsResponse>(`/queues/${queueName}/stats`)
    return response.data
  },

  async getQueueJobs(
    queueName: string,
    status: string = 'waiting',
    limit: number = 50
  ): Promise<QueueJobsResponse> {
    const response = await api.get<QueueJobsResponse>(`/queues/${queueName}/jobs`, {
      params: { status, limit },
    })
    return response.data
  },

  async pauseQueue(queueName: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/queues/${queueName}/pause`)
    return response.data
  },

  async resumeQueue(queueName: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/queues/${queueName}/resume`)
    return response.data
  },

  async removeJob(queueName: string, jobId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/queues/${queueName}/jobs/${jobId}`)
    return response.data
  },

  async cleanQueue(
    queueName: string,
    status: 'completed' | 'failed',
    age: number = 24
  ): Promise<{ success: boolean; message: string; jobsRemoved: number }> {
    const response = await api.post(`/queues/${queueName}/clean`, { status, age })
    return response.data
  },

  async retryFailedJobs(
    queueName: string,
    limit: number = 100
  ): Promise<{ success: boolean; message: string; retriedCount: number; totalFailed: number }> {
    const response = await api.post(`/queues/${queueName}/retry-failed`, { limit })
    return response.data
  },
}
